import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { throttle } from '@ember/runloop';
import Component from '@glimmer/component';

import type { StyleParams } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';

const styleParamsKeys = ['width', 'height', 'top', 'left'] as const;
type StyleParamsKey = (typeof styleParamsKeys)[number];
type StyleParamsSet = {
	[Property in StyleParamsKey as Property]: number;
};

interface Args {
	element?: HTMLElement;
	offset?: StyleParams;
}

export default class CommonFocusOverlayComponent extends Component<Args> {
	focusedNode: HTMLElement | null = null;
	overlayNode: HTMLElement | null = null;
	offset: StyleParamsSet | null = null;

	resizeObserver: ResizeObserver | null = null;
	observersHandler: (() => void) | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (args.element) {
			this.focusedNode = args.element;

			this.offset = styleParamsKeys.reduce((acc, param) => {
				acc[param] = this.args.offset?.[param] || 0;

				return acc;
			}, {} as StyleParamsSet);
		}
	}

	@action
	addObservers(node: HTMLElement): void {
		this.overlayNode = node;

		if (this.focusedNode && this.overlayNode) {
			this.observersHandler = () => {
				throttle(this, this.repositionElement, 200, false);
			};
			this.resizeObserver = new ResizeObserver(this.observersHandler);
			this.resizeObserver.observe(this.focusedNode);

			document.addEventListener('scroll', this.observersHandler);
		}
	}

	repositionElement() {
		if (this.focusedNode && this.overlayNode) {
			const rect = this.focusedNode.getBoundingClientRect();

			if (this.offset) {
				this.overlayNode.style.width = rect.width + this.offset.width + 'px';
				this.overlayNode.style.height = rect.height + this.offset.height + 'px';
				this.overlayNode.style.top = rect.top + this.offset.top + 'px';
				this.overlayNode.style.left = rect.left + this.offset.left + 'px';
			}
		}
	}

	override willDestroy(): void {
		super.willDestroy();
		if (this.focusedNode && this.overlayNode && this.observersHandler) {
			this.resizeObserver?.unobserve(this.focusedNode);
			document.removeEventListener('scroll', this.observersHandler);
		}
	}
	<template>
		<div class="focus-overlay" {{didInsert this.addObservers}}></div>
	</template>
}
